import { Injectable } from '@angular/core';
import { TransferConnectV2SettingService } from '@features/transfer-connect-v2/services/transfer-connect-v2-setting/transfer-connect-v2-setting.service';
import { LoyaltyProgramHelper } from '@rd/helpers/loyalty-program.helper';
import { DynamicListItem } from '@rd/shared-legacy/components/dynamic-list/dynamic-list-item.type';
import {
  CashBackToAccountMembership,
  CashBackToCardMembership,
  CashBackToPlatformMembership,
  GiftCardCheckoutFormData,
  GiftCardOrderItemData,
  GiftCardPhysicalAddressFormData,
  LoyaltyProgram,
  OrderItem,
  OrderItemType,
  PointsTransferMembership,
} from '@rd/types';
import dayjs from 'dayjs';

export interface DigitalGcRecipientDetails {
  firstName?: string;
  lastName?: string;
  email: string;
  phoneNumber?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class TransactionDetailsFormatterService {
  constructor(protected loyaltyProgramHelper: LoyaltyProgramHelper) {}

  giftCardRecipientDetailsFormat(
    data: OrderItem | GiftCardCheckoutFormData,
    giftCardType: OrderItemType,
  ): DynamicListItem[] | undefined {
    if (giftCardType === OrderItemType.GiftCardOrderItem) {
      const transformed: DigitalGcRecipientDetails =
        this.transformDigitalGcRecipientDetailsData(data);
      return this.digitalGcRecipientDetailsFormat(transformed);
    }

    if (giftCardType === OrderItemType.PhysicalGiftCardOrderItem) {
      const transformed: GiftCardPhysicalAddressFormData =
        this.transformPhysicalGcRecipientDetailsData(data);
      return this.physicalGcRecipientDetailsFormat(transformed);
    }
  }

  transformDigitalGcRecipientDetailsData(
    data: OrderItem | GiftCardCheckoutFormData,
  ): DigitalGcRecipientDetails {
    if ('attributes' in data) {
      // OrderItem type
      const giftCardOrderItemAttributes = data?.attributes;

      if (!giftCardOrderItemAttributes) {
        throw new Error('giftCardOrderItemAttributes is not defined');
      }

      const orderItemData =
        giftCardOrderItemAttributes.data as GiftCardOrderItemData;

      return {
        firstName: orderItemData.recipient.firstName,
        lastName: orderItemData.recipient.lastName,
        email: orderItemData.recipient.emailAddress,
        phoneNumber:
          'phoneNumber' in orderItemData.recipient
            ? orderItemData.recipient.phoneNumber
            : null,
      };
    } else {
      // GiftCardCheckoutFormData type
      const typeCasted: GiftCardCheckoutFormData =
        data as GiftCardCheckoutFormData;
      return {
        firstName: typeCasted.recipientFirstName,
        lastName: typeCasted.recipientLastName,
        email: typeCasted.email,
        phoneNumber: !!typeCasted.mobileNumber ? typeCasted.mobileNumber : null,
      };
    }
  }

  transformPhysicalGcRecipientDetailsData(
    data: OrderItem | GiftCardCheckoutFormData,
  ): GiftCardPhysicalAddressFormData {
    if ('attributes' in data) {
      // OrderItem type
      const giftCardOrderItemAttributes = data?.attributes;

      if (!giftCardOrderItemAttributes) {
        throw new Error('giftCardOrderItemAttributes is not defined');
      }

      const orderItemData =
        giftCardOrderItemAttributes.data as GiftCardOrderItemData;

      return {
        firstName: orderItemData.recipient.firstName,
        lastName: orderItemData.recipient.lastName,
        email: orderItemData.recipient.emailAddress,
        companyName: orderItemData.recipient?.companyName,
        phoneNumber: orderItemData.recipient?.phoneNumber,
        addressLine1: orderItemData.recipient?.address?.line1 || '',
        addressLine2: orderItemData.recipient?.address?.line2 || undefined,
        city: orderItemData.recipient?.address?.city || undefined,
        state: orderItemData.recipient?.address?.state || undefined,
        country: orderItemData.recipient?.address?.countryCode || undefined,
        postalCode: orderItemData.recipient?.address?.postalCode || '',
      };
    } else {
      // GiftCardCheckoutFormData type
      const typeCasted: GiftCardCheckoutFormData =
        data as GiftCardCheckoutFormData;
      return typeCasted.physicalAddressForm as GiftCardPhysicalAddressFormData;
    }
  }

  digitalGcRecipientDetailsFormat(
    details: DigitalGcRecipientDetails,
  ): DynamicListItem[] {
    const fullName: string = [details.firstName, details.lastName].join(' ');

    return [
      { value: fullName, cssClass: 'full-name' },
      { value: details.email, cssClass: 'email' },
    ].filter((obj) => obj.value) as DynamicListItem[];
  }

  physicalGcRecipientDetailsFormat(
    address: GiftCardPhysicalAddressFormData,
  ): DynamicListItem[] {
    const fullName: string = [address.firstName, address.lastName].join(' ');

    const state: string = [
      address.city,
      address.state,
      address.postalCode,
    ].join(', ');
    return [
      { value: fullName, cssClass: 'full-name' },
      { value: address.email, cssClass: 'email' },
      {
        value: address.phoneNumber,
        cssClass: 'phone-number',
      },
      {
        value: address.companyName,
        cssClass: 'company-name',
      },
      {
        value: address.addressLine1,
        cssClass: 'address-line-1',
      },
      {
        value: address.addressLine2,
        cssClass: 'address-line-2',
      },
      { value: state, cssClass: 'state' },
    ].filter((obj) => obj.value) as DynamicListItem[];
  }

  redeemCashToAccountDetails(
    redeemCashMembership: CashBackToAccountMembership,
  ): DynamicListItem[] {
    return [
      { translationKey: 'redeem_cash_v2.success.redeem_to_account_label' },
      {
        value: [
          redeemCashMembership.firstName,
          redeemCashMembership.lastName,
        ].join(' '),
      },
      { value: redeemCashMembership.details.bsbNumber },
      { value: redeemCashMembership.number },
    ];
  }

  redeemCashToCardDetails(
    redeemCashMembership: CashBackToCardMembership,
  ): DynamicListItem[] {
    return [
      {
        translationKey: 'redeem_cash_v2.success.redeem_to_card_label',
        value: { loyaltyProgram: redeemCashMembership.details?.groupName },
      },
      { value: redeemCashMembership.number },
    ];
  }

  redeemCashToPlatformDetails(
    redeemCashMembership: CashBackToPlatformMembership,
  ): DynamicListItem[] {
    return [
      { translationKey: 'redeem_cash_v2.success.redeem_to_platform_label' },
      {
        value: [
          redeemCashMembership.firstName,
          redeemCashMembership.lastName,
        ].join(' '),
      },
      { value: redeemCashMembership.number },
    ];
  }

  pointsTransferDetailsFormat(params: {
    loyaltyProgram: LoyaltyProgram;
    pointsTransferData: Partial<PointsTransferMembership>;
    processingDelay: number;
    tcv2Settings?: TransferConnectV2SettingService;
  }): DynamicListItem[] {
    const {
      loyaltyProgram,
      pointsTransferData,
      processingDelay,
      tcv2Settings,
    } = params;
    const memberDetailsItemsWithoutDob: DynamicListItem[] = [
      {
        translationKey: 'transfer_v2.review.membership_details_title',
        htmlTag: 'p',
        cssClass: 'membership-details-title',
        value: {
          loyaltyProgramName: loyaltyProgram.name,
          redeemDuration:
            this.loyaltyProgramHelper.getRedeemDurationText(processingDelay),
        },
      },
      {
        translationKey: pointsTransferData.number,
        htmlTag: 'p',
        cssClass: 'membership-details-information',
      },
      {
        translationKey: `${pointsTransferData.firstName} ${pointsTransferData.lastName}`,
        htmlTag: 'p',
        cssClass: 'membership-details-information',
      },
    ];

    return tcv2Settings?.shouldShowDobFieldInLinkForm(
      loyaltyProgram.transferConnectIdentifier as string,
    )
      ? [
          ...memberDetailsItemsWithoutDob,
          ...this.getDateOfBirth(pointsTransferData),
        ]
      : [...memberDetailsItemsWithoutDob];
  }

  protected getDateOfBirth(
    pointsTransferData: Partial<PointsTransferMembership>,
  ): DynamicListItem[] {
    return [
      {
        translationKey: `${dayjs(pointsTransferData.dateOfBirth).format(
          'YYYY/MM/DD',
        )}`,
        htmlTag: 'p',
        cssClass: 'membership-details-information',
      },
    ];
  }
}
